import React, { useEffect, useState, useMemo } from 'react'
import axios from 'axios'
import { useParams, useNavigate, Link } from 'react-router-dom'
import BASE_URL from '../../../config/config'
import { jwtDecode } from "jwt-decode"
import { FiCheckCircle, FiAlertTriangle, FiCode, FiEdit3 } from 'react-icons/fi'
import { FaRocket, FaPalette } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import Loader from '../../../components/Loader'
import { claves } from '../../../queries/cliente'

const SingleProyecto = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [guardandoFacturado, setGuardandoFacturado] = useState(false)

  const token = localStorage.getItem("AuthToken")
  let tokenInfo = {}
  if (token) {
    try { tokenInfo = jwtDecode(token) } catch { tokenInfo = {} }
  }
  const isAdmin = tokenInfo.role === 'admin'

  const consultaProyecto = useQuery({
    queryKey: claves.proyecto(id),
    queryFn: async () => (await axios.get(`${BASE_URL}/proyecto/${id}`)).data.proyecto,
    enabled: Boolean(id),
  })

  const proyecto = consultaProyecto.data || null
  const loading = consultaProyecto.isPending
  const error = consultaProyecto.isError ? t('proyectos.errorCargar') : null

  const setProyecto = (datos) => queryClient.setQueryData(claves.proyecto(id), datos)

  // Redirige si no es admin ni usuario autorizado
  useEffect(() => {
    if (!loading && proyecto) {
      if (tokenInfo.role !== "admin" && tokenInfo.usuario !== proyecto.usuario_autorizado) {
        navigate("/dashboard")
      }
    }
  }, [loading, proyecto, tokenInfo, navigate])

  // Guardar cambios (p.ej. facturado)
  const updateCampo = async (field, value) => {
    if (!proyecto) return
    const payload = {
      prototipo: proyecto.prototipo,
      diseno: proyecto.diseno,
      desarrollo: proyecto.desarrollo,
      contenido: proyecto.contenido,
      comprobacion_final: proyecto.comprobacion_final,
      usuario_autorizado: proyecto.usuario_autorizado,
      facturado: typeof proyecto.facturado === 'undefined' ? 0 : proyecto.facturado,
      [field]: value
    }
    try {
      const res = await axios.put(`${BASE_URL}/actualizar-estados/${proyecto.id}`, payload)
      setProyecto(res.data.proyecto)
    } catch (e) {
      console.error('Error al actualizar:', e)
    }
  }

  const toggleFacturado = async () => {
    setGuardandoFacturado(true)
    const current = Number(proyecto?.facturado || 0)
    await updateCampo('facturado', current === 1 ? 0 : 1)
    setGuardandoFacturado(false)
  }

  const tipo = useMemo(() => (proyecto?.tipo_de_proyecto || '').toLowerCase(), [proyecto])
  const isDiseno = tipo === 'diseno'
  const isMarketing = tipo === 'marketing'

  const isFacturado = useMemo(
    () => Number(proyecto?.facturado ?? 0) === 1,
    [proyecto?.facturado]
  )

  const isListo = useMemo(() => {
    if (isDiseno) return proyecto?.diseno === 'aceptado' && isFacturado
    if (isMarketing) return proyecto?.desarrollo === 'aceptado' && isFacturado
    return proyecto?.comprobacion_final === 'aceptado'
  }, [isDiseno, isMarketing, proyecto?.diseno, proyecto?.desarrollo, proyecto?.comprobacion_final, isFacturado])

  const timelineClass = useMemo(() => {
    if (isDiseno) return 'timeline-proyecto timeline-proyecto-diseno'
    if (isMarketing) return 'timeline-proyecto timeline-proyecto-marketing'
    return 'timeline-proyecto'
  }, [isDiseno, isMarketing])

  if (loading) return <div className="loader-fullscreen"><Loader/></div>
  if (error) return <div>{error}</div>
  if (!proyecto) return <div>{t('proyectos.noEncontrado')}</div>

  const tipoLabel = t(`tipoProyecto.${proyecto.tipo_de_proyecto}`, { defaultValue: t('tipoProyecto.no_especificado') })

  return (
    <main className='site-width'>
      <button className="boton-volver" onClick={() => navigate(isAdmin ? '/dashboard/gestion-proyectos' : '/dashboard/proyectos')}>
        ← {t('comun.volver')}
      </button>

      <div className="single-proyecto">
        <div className={`proy-hero ${isListo ? 'proy-hero--listo' : ''}`}>
          <div className="proy-hero-info">
            <h1>{isAdmin ? proyecto.nombre : t('proyectos.bienvenidaProyecto', { nombre: proyecto.nombre })}</h1>
            <div className="proy-hero-meta-row">
              <span className="proy-badge-tipo">{tipoLabel}</span>
              {isAdmin && (
                proyecto.usuario_autorizado ? (
                  <span className="proy-hero-cliente">
                    {t('proyectos.cliente')} <Link to={`/dashboard/usuarios/${proyecto.usuario_autorizado}`}>@{proyecto.usuario_autorizado}</Link>
                  </span>
                ) : (
                  <span className="proy-hero-cliente proy-hero-cliente-vacio">{t('proyectos.sinClienteAsignado')}</span>
                )
              )}
            </div>
            {!isAdmin && (
              <p className="proy-hero-ayuda">{t('proyectos.ayudaCliente')}</p>
            )}
          </div>
        </div>

        <div className="grid-timeline">
          <div className="texto-pre-timeline">
            {isListo
              ? <p className='fin-proyecto'><FiCheckCircle /> {t('proyectos.proyectoFinalizado')}</p>
              : <p className='warning-proyecto'><FiAlertTriangle /> {proyecto.prototipo === 'sin_ejecutar' ? t('proyectos.noEmpezado') : t('proyectos.noListo')}</p>
            }
          </div>

          <div className={timelineClass}>
            {/* -------- VISTA: DESARROLLO (completa) -------- */}
            {!isDiseno && !isMarketing && (
              <>
                <div className={`item-timeline proyecto-${proyecto.prototipo}`}>
                  <div className='icono-timeline'>
                    <FaRocket />
                    {proyecto.prototipo === "aceptado" && (
                       <img src="/images/controlar.svg" className='check-aceptado' alt="" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>{t('proyectos.fases.prototipo.titulo')}</h3>
                    <p>{t('proyectos.fases.prototipo.texto')}</p>
                    {proyecto.prototipo === "en_proceso" && <p className='texto-timeline-en_proceso'>{t('proyectos.enProceso')}</p>}
                    {proyecto.prototipo === "aceptado" && <p className='texto-timeline-aceptado'>{t('proyectos.validado')}</p>}
                  </div>
                </div>

                <div className={`item-timeline proyecto-${proyecto.diseno}`}>
                  <div className='icono-timeline'>
                    <FaPalette />
                    {proyecto.diseno === "aceptado" && (
                       <img src="/images/controlar.svg" className='check-aceptado' alt="" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>{t('proyectos.fases.diseno.titulo')}</h3>
                    <p>{t('proyectos.fases.diseno.texto')}</p>
                    {proyecto.diseno === "en_proceso" && <p className='texto-timeline-en_proceso'>{t('proyectos.enProceso')}</p>}
                    {proyecto.diseno === "aceptado" && <p className='texto-timeline-aceptado'>{t('proyectos.validado')}</p>}
                  </div>
                </div>

                <div className={`item-timeline proyecto-${proyecto.desarrollo}`}>
                  <div className='icono-timeline'>
                    <FiCode />
                    {proyecto.desarrollo === "aceptado" && (
                       <img src="/images/controlar.svg" className='check-aceptado' alt="" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>{t('proyectos.fases.desarrollo.titulo')}</h3>
                    <p>{t('proyectos.fases.desarrollo.texto')}</p>
                    {proyecto.desarrollo === "en_proceso" && <p className='texto-timeline-en_proceso'>{t('proyectos.enProceso')}</p>}
                    {proyecto.desarrollo === "aceptado" && <p className='texto-timeline-aceptado'>{t('proyectos.validado')}</p>}
                  </div>
                </div>

                <div className={`item-timeline proyecto-${proyecto.contenido}`}>
                  <div className='icono-timeline'>
                    <FiEdit3 />
                    {proyecto.contenido === "aceptado" && (
                       <img src="/images/controlar.svg" className='check-aceptado' alt="" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>{t('proyectos.fases.contenido.titulo')}</h3>
                    <p>{t('proyectos.fases.contenido.texto')}</p>
                    {proyecto.contenido === "en_proceso" && <p className='texto-timeline-en_proceso'>{t('proyectos.enProceso')}</p>}
                    {proyecto.contenido === "aceptado" && <p className='texto-timeline-aceptado'>{t('proyectos.validado')}</p>}
                  </div>
                </div>

                <div className={`item-timeline proyecto-${proyecto.comprobacion_final}`}>
                  <div className='icono-timeline'>
                    <FiCheckCircle />
                    {proyecto.comprobacion_final === "aceptado" && (
                       <img src="/images/controlar.svg" className='check-aceptado' alt="" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>{t('proyectos.fases.validacion.titulo')}</h3>
                    <p>{t('proyectos.fases.validacion.texto')}</p>
                    {proyecto.comprobacion_final === "en_proceso" && <p className='texto-timeline-en_proceso'>{t('proyectos.enProceso')}</p>}
                    {proyecto.comprobacion_final === "aceptado" && <p className='texto-timeline-aceptado'>{t('proyectos.validado')}</p>}
                  </div>
                </div>
              </>
            )}

            {/* -------- VISTA: DISEÑO -------- */}
            {isDiseno && (
              <>
                <div className={`item-timeline proyecto-${proyecto.diseno}`}>
                  <div className='icono-timeline'>
                    <FaPalette />
                    {proyecto.diseno === "aceptado" && (
                       <img src="/images/controlar.svg" className='check-aceptado' alt="" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>{t('proyectos.fases.disenoSolo.titulo')}</h3>
                    <p>{t('proyectos.fases.disenoSolo.texto')}</p>
                    {proyecto.diseno === "en_proceso" && <p className='texto-timeline-en_proceso'>{t('proyectos.enProceso')}</p>}
                    {proyecto.diseno === "aceptado" && <p className='texto-timeline-aceptado'>{t('proyectos.validado')}</p>}
                  </div>
                </div>

                <div className={`item-timeline proyecto-facturado`}>
                  <div className='icono-timeline'>
                    <img src="/images/pago.svg" alt={t('proyectos.iconoFactura')} />
                  </div>
                  <div className="texto-timeline">
                    <h3>{t('proyectos.facturado')}</h3>
                    <p>{t('proyectos.facturadoEstado')}</p>
                    {isAdmin ? (
                      <button
                        type="button"
                        className={`toggle-facturado ${isFacturado ? 'on' : 'off'}`}
                        onClick={toggleFacturado}
                        disabled={guardandoFacturado}
                      >
                        {guardandoFacturado ? t('proyectos.guardando') : (isFacturado ? t('proyectos.si') : t('proyectos.no'))}
                      </button>
                    ) : (
                      <p><b>{isFacturado ? t('proyectos.si') : t('proyectos.no')}</b></p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* -------- VISTA: MARKETING -------- */}
            {isMarketing && (
              <>
                <div className={`item-timeline proyecto-${proyecto.desarrollo}`}>
                  <div className='icono-timeline'>
                    <FiCode />
                    {proyecto.desarrollo === "aceptado" && (
                       <img src="/images/controlar.svg" className='check-aceptado' alt="" />
                    )}
                  </div>
                  <div className="texto-timeline">
                    <h3>{t('proyectos.fases.realizacion.titulo')}</h3>
                    <p>{t('proyectos.fases.realizacion.texto')}</p>
                    {proyecto.desarrollo === "en_proceso" && <p className='texto-timeline-en_proceso'>{t('proyectos.enProceso')}</p>}
                    {proyecto.desarrollo === "aceptado" && <p className='texto-timeline-aceptado'>{t('proyectos.validado')}</p>}
                  </div>
                </div>

                <div className={`item-timeline proyecto-facturado`}>
                  <div className='icono-timeline'>
                    <img src="/images/pago.svg" alt={t('proyectos.iconoFactura')} />
                  </div>
                  <div className="texto-timeline">
                    <h3>{t('proyectos.facturado')}</h3>
                    <p>{t('proyectos.facturadoEstado')}</p>
                    {isAdmin ? (
                      <button
                        type="button"
                        className={`toggle-facturado ${isFacturado ? 'on' : 'off'}`}
                        onClick={toggleFacturado}
                        disabled={guardandoFacturado}
                      >
                        {guardandoFacturado ? t('proyectos.guardando') : (isFacturado ? t('proyectos.si') : t('proyectos.no'))}
                      </button>
                    ) : (
                      <p><b>{isFacturado ? t('proyectos.si') : t('proyectos.no')}</b></p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}

export default SingleProyecto
